<script setup lang="ts">
import type { OfferTermsContent } from '@promotion/types'
import type { HeroContent } from '@nimbus/types'
import { usePartner } from '#imports'

defineOptions({
    name: 'StickyTerms'
})

export interface StickyTermsProps {
    backgroundColor?: string
    title?: string
    isLandingPage?: boolean
    heroData?: HeroContent
    forceFullTerms?: boolean
    enableMultiple?: boolean
    stickyConfig?: {
        container: string
        minHeight: number
        offset: number
        backgroundColor: string
    }
}

const props = defineProps<StickyTermsProps>()

const { isFromPartner } = usePartner()
const { signupBundles, defaultBundle } = useSignupBundles()
const { isExisting } = useUserInfo()

const showFullTerms = computed(() => props.forceFullTerms || isFromPartner.value)

const terms = computed<OfferTermsContent[]>(() => {
    const termsArray = []

    if (props.heroData?.short_terms || props.heroData?.long_terms) {
        let heroTerms1 = props.heroData?.short_terms
        let heroTerms2 = props.heroData?.short_terms_2

        if (showFullTerms.value) {
            heroTerms1 = props.heroData?.long_terms || props.heroData?.short_terms
            heroTerms2 = props.heroData?.long_terms_2 || props.heroData?.short_terms_2
        }

        if (heroTerms1) termsArray.push({ label: props.heroData?.label, text: heroTerms1 })
        if (heroTerms2) termsArray.push({ label: props.heroData?.label_2, text: heroTerms2 })
    }

    // This makes sure that terms are only shown in game hereos with short_ og long_terms (when not from partner or forced)
    if (props.heroData?.design === 'game' && (!showFullTerms.value || isExisting.value)) {
        return termsArray
    }

    if (!termsArray.length && signupBundles.value?.length) {
        if (props.enableMultiple) {
            signupBundles.value.forEach((bundle) => {
                const text = showFullTerms.value ? bundle.longTerms : bundle.shortTerms

                termsArray.push({ label: bundle.label, title: bundle.title, text })
            })

            return termsArray
        }

        if (!defaultBundle.value) return termsArray

        let relevantTerms = showFullTerms.value
            ? defaultBundle.value.longTerms
            : defaultBundle.value.shortTerms

        // Terms are combined on landingPages to make sure we are total and absolutly compliant!-_-!
        if (props.isLandingPage) {
            relevantTerms = showFullTerms.value
                ? `${defaultBundle.value.shortTerms}<br><br>${defaultBundle.value.longTerms}`
                : defaultBundle.value.shortTerms
        }

        const label = defaultBundle.value.label
        termsArray.push({ label, text: relevantTerms })
    }

    return termsArray
})
</script>

<template>
    <PromotionTerms
        v-if="terms.length"
        class="container sticky-terms"
        :class="{ 'sticky-terms--landing-page': isLandingPage }"
        :terms="terms"
        :show-title="true"
        :title="title"
        :type="showFullTerms ? 'long' : 'short'"
        :sticky-config="stickyConfig"
        style="margin-top: 0"
    />
</template>

<style scoped lang="scss">
.sticky-terms {
    color: var(--sticky-terms-color, var(--on-background));
    z-index: 6;

    :deep(.offer-terms__text) {
        line-height: 1.4;
    }

    &--landing-page {
        color: $white;
        padding-bottom: 1rem;
        background-color: transparent;
        margin-left: auto;
        margin-right: auto;
        max-width: 1024px;
        padding-left: 15px;
        padding-right: 15px;

        :deep(.offer-terms__text b) {
            display: block;
            font-weight: bold;
        }

        :deep(.offer-terms__text a) {
            color: $white;
        }

        :deep(.offer-terms__text) {
            line-height: 1.2;
        }
    }
}
</style>
